/**
 * ══════════════════════════════════════════════════════════════
 * DiagnosticPanel — Componente do Design System para Diagnósticos
 * ══════════════════════════════════════════════════════════════
 *
 * Exibe análise de causa raiz derivada de um insight analítico.
 * Mostra causas prováveis, entidades afetadas e ações recomendadas.
 *
 * Uso: import { DiagnosticPanel } from '@/components/system';
 * ══════════════════════════════════════════════════════════════
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, ShieldAlert, AlertTriangle, Info, ArrowRight,
  ChevronDown, ChevronUp, Target, Lightbulb, BarChart3,
  CheckCircle2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import type { RootCauseAnalysis, ProbableCause, AffectedEntity, DiagnosticAction } from '@/types/diagnostics';
import { CAUSE_CATEGORY_LABELS, ACTION_PRIORITY_LABELS } from '@/types/diagnostics';

// ─── Severity Config ────────────────────────────────────────

const SEVERITY_STYLES: Record<string, {
  icon: React.ElementType;
  headerClass: string;
  badgeClass: string;
}> = {
  critical: {
    icon: ShieldAlert,
    headerClass: 'bg-destructive/5 border-destructive/20',
    badgeClass: 'bg-destructive/10 text-destructive border-destructive/30',
  },
  warning: {
    icon: AlertTriangle,
    headerClass: 'bg-orange-500/5 border-orange-500/20',
    badgeClass: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
  },
  info: {
    icon: Info,
    headerClass: 'bg-primary/5 border-primary/20',
    badgeClass: 'bg-primary/10 text-primary border-primary/30',
  },
};

const PRIORITY_STYLES: Record<string, string> = {
  immediate: 'bg-destructive/10 text-destructive border-destructive/30',
  short_term: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
  medium_term: 'bg-primary/10 text-primary border-primary/30',
};

// ─── Props ──────────────────────────────────────────────────

export interface DiagnosticPanelProps {
  diagnostic: RootCauseAnalysis;
  /** Called when panel is dismissed */
  onClose?: () => void;
  className?: string;
}

// ─── Component ──────────────────────────────────────────────

export function DiagnosticPanel({ diagnostic, onClose, className = '' }: DiagnosticPanelProps) {
  const navigate = useNavigate();
  const [expandedCause, setExpandedCause] = useState<string | null>(
    diagnostic.probableCauses[0]?.id ?? null
  );

  const severity = SEVERITY_STYLES[diagnostic.severity] || SEVERITY_STYLES.info;
  const SevIcon = severity.icon;

  return (
    <div className={`rounded-[10px] border bg-card overflow-hidden ${className}`}>
      {/* ── Header ── */}
      <div className={`p-4 border-b ${severity.headerClass}`}>
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-background/80">
            <Search className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold">Diagnóstico de Causa Raiz</h3>
              <Badge variant="outline" className={severity.badgeClass}>
                <SevIcon className="h-3 w-3 mr-1" />
                {diagnostic.severity === 'critical' ? 'Crítico' : diagnostic.severity === 'warning' ? 'Atenção' : 'Info'}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{diagnostic.summary}</p>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors text-xs shrink-0">
              ✕
            </button>
          )}
        </div>

        {/* Confidence bar */}
        <div className="mt-3 flex items-center gap-3">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Confiança</span>
          <Progress value={diagnostic.confidenceScore} className="flex-1 h-1.5" />
          <span className="text-xs font-semibold tabular-nums">{diagnostic.confidenceScore}%</span>
        </div>
      </div>

      {/* ── Probable Causes ── */}
      <div className="p-4 space-y-2">
        <div className="flex items-center gap-2 mb-3">
          <Target className="h-3.5 w-3.5 text-muted-foreground" />
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Causas Prováveis ({diagnostic.probableCauses.length})
          </h4>
        </div>

        {diagnostic.probableCauses.map((cause) => (
          <CauseItem
            key={cause.id}
            cause={cause}
            isExpanded={expandedCause === cause.id}
            onToggle={() => setExpandedCause(prev => prev === cause.id ? null : cause.id)}
          />
        ))}
      </div>

      {/* ── Affected Entities ── */}
      {diagnostic.affectedEntities.length > 0 && (
        <div className="px-4 pb-4">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="h-3.5 w-3.5 text-muted-foreground" />
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Entidades Impactadas
            </h4>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {diagnostic.affectedEntities.map((entity) => (
              <EntityBadge key={entity.id} entity={entity} navigate={navigate} />
            ))}
          </div>
        </div>
      )}

      {/* ── Recommended Actions ── */}
      {diagnostic.recommendedActions.length > 0 && (
        <div className="border-t bg-muted/20 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="h-3.5 w-3.5 text-muted-foreground" />
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Ações Recomendadas
            </h4>
          </div>
          <div className="space-y-2">
            {diagnostic.recommendedActions.map((action) => (
              <ActionItem key={action.id} action={action} navigate={navigate} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────

function CauseItem({ cause, isExpanded, onToggle }: {
  cause: ProbableCause;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="rounded-lg border bg-background transition-all">
      <button
        onClick={onToggle}
        className="w-full text-left flex items-center gap-3 p-3 hover:bg-muted/30 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium leading-tight">{cause.label}</span>
            <Badge variant="outline" className="text-xs shrink-0">
              {CAUSE_CATEGORY_LABELS[cause.category]}
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs font-semibold tabular-nums text-muted-foreground">
            {cause.confidence}%
          </span>
          {isExpanded ? (
            <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="px-3 pb-3 space-y-2">
          <p className="text-xs text-muted-foreground leading-relaxed">{cause.description}</p>

          {cause.evidence.length > 0 && (
            <div className="space-y-1">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Evidências</span>
              {cause.evidence.map((ev, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <CheckCircle2 className="h-3 w-3 text-primary/60 shrink-0" />
                  <span className="text-muted-foreground">{ev.label}:</span>
                  <span className="font-medium">{ev.value}</span>
                </div>
              ))}
            </div>
          )}

          {/* Confidence bar for individual cause */}
          <div className="flex items-center gap-2 pt-1">
            <span className="text-xs text-muted-foreground">Confiança</span>
            <Progress value={cause.confidence} className="flex-1 h-1" />
          </div>
        </div>
      )}
    </div>
  );
}

function EntityBadge({ entity, navigate }: { entity: AffectedEntity; navigate: ReturnType<typeof useNavigate> }) {
  const impactColors = {
    high: 'bg-destructive/10 text-destructive border-destructive/30',
    medium: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
    low: 'bg-primary/10 text-primary border-primary/30',
  };

  return (
    <button
      onClick={() => entity.drillDownRoute && navigate(entity.drillDownRoute)}
      disabled={!entity.drillDownRoute}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-medium transition-colors
        ${impactColors[entity.impact]}
        ${entity.drillDownRoute ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
    >
      {entity.name}
      {entity.drillDownRoute && <ArrowRight className="h-2.5 w-2.5" />}
    </button>
  );
}

function ActionItem({ action, navigate }: { action: DiagnosticAction; navigate: ReturnType<typeof useNavigate> }) {
  return (
    <div
      role={action.drillDownRoute ? 'button' : undefined}
      onClick={() => action.drillDownRoute && navigate(action.drillDownRoute)}
      className={`flex items-start gap-3 p-2.5 rounded-lg border bg-background transition-colors
        ${action.drillDownRoute ? 'cursor-pointer hover:bg-muted/30' : ''}`}
    >
      <Badge variant="outline" className={`text-xs shrink-0 mt-0.5 ${PRIORITY_STYLES[action.priority]}`}>
        {ACTION_PRIORITY_LABELS[action.priority]}
      </Badge>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium leading-tight">{action.title}</p>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{action.description}</p>
      </div>
      {action.drillDownRoute && <ArrowRight className="h-3 w-3 shrink-0 mt-1 text-muted-foreground/40" />}
    </div>
  );
}

export default DiagnosticPanel;
